import { CSSProperties } from 'react';
import { Promotion } from 'reducers/playerPromos';

import { buildPromoDisplay, getCurrentWeekEndDateLabel } from './promoDisplay';
import { getPromoAccountBannerCopy } from './promoMessaging';
import { getPromoVisualTheme, getTierProgressFill, PROMO_GOLD_METAL } from './promoTheme';

type AccountPromoBannerProps = {
  promo: Promotion;
  onGetMoreInfo: () => void;
};

const AccountPromoBanner = ({ promo, onGetMoreInfo }: AccountPromoBannerProps) => {
  const display = buildPromoDisplay(promo);
  const theme = getPromoVisualTheme(display.format, promo.player_facing_config?.theme);
  const remaining = Math.max(display.targetValue - display.progressValue, 0);
  const remainingLabel =
    display.hybridDetails?.remainingLabel ||
    display.remainingLabel ||
    (display.format === 'weekly_timeback_credit'
      ? `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : `${Number.isInteger(remaining) ? remaining.toFixed(0) : remaining.toFixed(1)}h`);
  const copy = getPromoAccountBannerCopy({
    promoName: promo.promo_name,
    remainingLabel,
    display,
    playerFacingConfig: promo.player_facing_config
  });

  // Only formats that reset every week have a week to end; total-hours formats do not.
  const weekEndLabel = WEEKLY_FORMATS.has(display.format) ? getCurrentWeekEndDateLabel(promo) : '';
  // Two-tier formats (EN-11291): the top tier dresses the medallion in the $100K gold.
  const isTopTier = display.tierState === 'bonusAchieved';
  // Never announce a full meter while the target is still short (14.999 h of 15 h is 99,
  // not a rounded-up 100 beside "1m" left).
  const announcedPct =
    display.progressValue < display.targetValue
      ? Math.min(99, Math.floor(display.progressPct))
      : Math.round(display.progressPct);

  return (
    <div
      data-testid='account-promo-banner'
      style={{
        ...promoBannerStyle,
        background: theme.background,
        borderColor: theme.borderColor,
        boxShadow: theme.boxShadow,
        color: theme.iconColor
      }}>
      {theme.detailsCardFrameStyle && (
        <span
          aria-hidden='true'
          data-testid='account-promo-banner-frame'
          style={theme.detailsCardFrameStyle}
        />
      )}
      <div
        aria-hidden='true'
        data-testid='account-promo-banner-icon'
        style={{
          ...promoBannerIconWrap,
          background: isTopTier ? PROMO_GOLD_METAL : theme.iconBackground,
          borderColor: isTopTier ? 'rgba(59, 32, 8, 0.85)' : theme.iconBorderColor,
          color: isTopTier ? GOLD_INK : theme.iconColor
        }}>
        {/* The ticket spans y 3 to 17 of its 24-unit box; the -2 offset centres it. */}
        <svg width='20' height='20' viewBox='0 -2 24 24' fill='currentColor'>
          <path d='M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z' />
        </svg>
      </div>
      <div style={{ ...promoBannerHeaderStyle, color: theme.iconColor }}>{copy.title}</div>
      <div style={promoBannerContentStyle}>
        <div
          role='progressbar'
          aria-label={`${copy.title} progress`}
          aria-valuenow={announcedPct}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{ ...promoBannerTrackStyle, background: theme.trackColor }}>
          <div
            style={{
              ...promoBannerFillStyle,
              width: `${display.progressPct}%`,
              background: getTierProgressFill(theme, display.tierState)
            }}
          />
        </div>
        <span style={{ ...promoBannerCopyStyle, color: theme.iconColor }}>{copy.body}</span>
        <div style={promoBannerFooterStyle}>
          <button
            type='button'
            className='account-promo-banner-cta'
            style={{
              ...promoBannerCtaStyle,
              background: theme.actionBackground,
              borderColor: theme.accentMuted,
              color: theme.iconColor
            }}
            onClick={onGetMoreInfo}>
            {copy.actionLabel}
          </button>
          {weekEndLabel && (
            <span style={{ ...promoBannerMetaStyle, color: theme.iconColor }}>
              Week ends {weekEndLabel}
            </span>
          )}
        </div>
      </div>
      <style>{promoBannerCss}</style>
    </div>
  );
};

export default AccountPromoBanner;

const GOLD_INK = '#3b2008';

const WEEKLY_FORMATS = new Set([
  'weekly_qualification_freeroll',
  'hybrid_freeroll_qualification',
  'weekly_timeback_credit',
  'weekly_cash_back'
]);

/* Press feedback only where it helps: a pointer that can hover, and never under reduced motion. */
const promoBannerCss = `
  .account-promo-banner-cta { transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1); }
  .account-promo-banner-cta:active { transform: scale(0.97); }
  .account-promo-banner-cta:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) {
    .account-promo-banner-cta { transition: none; }
    .account-promo-banner-cta:active { transform: none; }
  }
`;

/* The medallion and the title share the first row, centred on each other; the bar, body and
   actions hang from the title's left edge. */
const promoBannerStyle: CSSProperties = {
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '38px minmax(0, 1fr)',
  columnGap: '12px',
  rowGap: '8px',
  alignItems: 'center',
  padding: '12px 13px 13px',
  marginBottom: 16,
  background: 'linear-gradient(180deg, #e9d5a3 0%, #d4b478 100%)',
  border: '1px solid #4a230a',
  borderRadius: 6,
  boxShadow:
    '0 0 0 1px rgba(74, 35, 10, 0.4), 0 4px 10px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 235, 200, 0.55)',
  color: '#1a0e06'
};

const promoBannerContentStyle: CSSProperties = {
  gridColumn: 2,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column'
};

const promoBannerTrackStyle: CSSProperties = {
  height: 4,
  borderRadius: 999,
  overflow: 'hidden'
};

const promoBannerFillStyle: CSSProperties = {
  height: '100%',
  borderRadius: 999,
  transition: 'width 0.3s ease'
};

const promoBannerFooterStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '6px 12px',
  marginTop: 10
};

const promoBannerMetaStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 600,
  fontSize: '10.5px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontVariantNumeric: 'tabular-nums',
  opacity: 0.8,
  whiteSpace: 'nowrap'
};

const promoBannerHeaderStyle: CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '13px',
  color: '#1a0e06',
  letterSpacing: '0.04em',
  lineHeight: 1.15,
  textTransform: 'uppercase',
  textWrap: 'balance',
  textShadow: '0 1px 0 rgba(255, 235, 200, 0.45)'
} as CSSProperties;

const promoBannerIconWrap: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  width: 38,
  height: 38,
  borderRadius: '50%',
  background: 'radial-gradient(circle at 35% 30%, #ffe28a 0%, #e8b94a 50%, #a67514 100%)',
  border: '1.5px solid #1a0e06',
  color: '#1a0e06',
  display: 'grid',
  placeItems: 'center',
  boxShadow: 'inset 0 1px 0 rgba(255, 250, 210, 0.65), 0 2px 5px rgba(108, 68, 12, 0.4)',
  flexShrink: 0
};

// pre-line honours a line break written into the Admin copy, so "Play 14h more to make it
// $50" can start its own line instead of wrapping mid-sentence (demo 2026-10-28). Oswald, as in
// the entry modal: the 10.5px typewriter face was hard to read on the parchment (Jason, 2026-10-28).
const promoBannerCopyStyle: CSSProperties = {
  marginTop: 8,
  whiteSpace: 'pre-line',
  fontFamily: "'Oswald', sans-serif",
  fontSize: '13.5px',
  fontWeight: 500,
  color: '#2b1406',
  letterSpacing: '0.01em',
  lineHeight: 1.35,
  fontVariantNumeric: 'tabular-nums'
};

const promoBannerCtaStyle: CSSProperties = {
  minHeight: 28,
  padding: '5px 12px',
  background: 'rgba(74, 35, 10, 0.18)',
  border: '1px solid rgba(74, 35, 10, 0.55)',
  borderRadius: 4,
  color: '#1a0e06',
  fontFamily: "'Oswald', sans-serif",
  fontWeight: 700,
  fontSize: '10.5px',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  textShadow: '0 1px 0 rgba(255, 235, 200, 0.45)'
};
